import {
  type SubmitSettings,
  SubmitSuccessAction,
} from '@wix/ambassador-forms-v4-form/types';
import { type FormSubmission } from '@wix/ambassador-forms-v4-submission/types';
import { submitFormClick as submitFormBi } from '@wix/bi-logger-forms-data/v2';
import {
  EXPERIMENTS,
  useBi,
  useExperiments,
  usePanorama,
  useSubmitSuccessTrigger,
  useTranslation,
} from '@wix/form-fields';
import { type ValidationError } from '@wix/form-validator';
import { useCallback } from 'react';

import { useSubmitStatus } from '../../contexts/submit-status-context';
import { INTERACTIONS } from '../../features/monitoring/interactions';
import { type OpenPopup } from '../../form-actions/open-popup';
import { type FormValues } from '../../types';
import { BiFormSubmissionStatus } from '../../types/bi';

import {
  getCheckoutMessage,
  getErrorCode,
  getErrorList,
  getFieldCustomErrorMessages,
  getFormCustomErrorMessage,
  redirectToExternalUrl,
} from './utils';

interface SubmitProps {
  values: FormValues;
  submitAction: (values: FormValues) => Promise<FormSubmission | undefined>;
  onSubmit?: () => void;
  onSubmitSuccess?: (response: FormSubmission) => void;
  onSubmitFailure?: (error: unknown) => void;
  resetFormValues?: () => void;
  validateAndScrollToError: (values: FormValues) => Promise<ValidationError[]>;
  submitSettings?: SubmitSettings;
  navigateToCheckout?: Function;
  openPopup?: OpenPopup;
  handleStepChange: (step: number) => void;
  onValidate: (newErrors: ValidationError[]) => void;
  handleScrollToFirstError?: (errors: ValidationError[]) => void;
}

export type SubmitForm = () => Promise<FormSubmission | undefined>;

export const useSubmit = ({
  validateAndScrollToError,
  submitAction,
  onSubmit,
  onSubmitSuccess,
  onSubmitFailure,
  resetFormValues,
  values,
  submitSettings,
  handleStepChange,
  navigateToCheckout,
  openPopup,
  onValidate,
  handleScrollToFirstError,
}: SubmitProps): {
  submitForm: SubmitForm;
} => {
  const {
    setSubmitSuccessMessage,
    setSubmitFailureCode,
    setSubmitFailureMessage,
  } = useSubmitStatus();

  const { t } = useTranslation();
  const { report } = useBi();
  const fireSubmitSuccess = useSubmitSuccessTrigger();
  const panorama = usePanorama();
  const { isExperimentEnabled } = useExperiments();
  const focusOnSubmitSuccess = !isExperimentEnabled(
    EXPERIMENTS.SUBMIT_SUCCESS_NO_FOCUS,
  );

  const reportSubmitFormBi = useCallback(
    ({ status, id }: { status: BiFormSubmissionStatus; id?: string | null }) =>
      report(
        submitFormBi({
          submission_status: status,
          submission_id: id ?? undefined,
        }),
      ),
    [report],
  );

  const handleSubmitSuccess = useCallback(
    (submission: FormSubmission) => {
      const action = submitSettings?.submitSuccessAction;
      const checkoutId = submission?.orderDetails?.checkoutId;

      if (checkoutId && navigateToCheckout) {
        setSubmitSuccessMessage(
          getCheckoutMessage(t('submit.checkout.message')),
        );
        navigateToCheckout(checkoutId);
      } else if (action === SubmitSuccessAction.THANK_YOU_MESSAGE) {
        setSubmitSuccessMessage(
          submitSettings?.thankYouMessageOptions?.richContent,
        );
        if (submitSettings?.thankYouMessageOptions?.durationInSeconds) {
          setTimeout(() => {
            setSubmitSuccessMessage(undefined);
            if (focusOnSubmitSuccess) {
              handleStepChange(0);
            }
          }, submitSettings?.thankYouMessageOptions?.durationInSeconds * 1000);
        } else if (focusOnSubmitSuccess) {
          handleStepChange(0);
        }
      } else if (action === SubmitSuccessAction.REDIRECT) {
        redirectToExternalUrl(submitSettings?.redirectOptions);
      } else if (action === SubmitSuccessAction.POPUP) {
        setTimeout(() => {
          openPopup?.(submitSettings?.popupOptions?.popupId ?? '', {});
        }, 500);
      }

      setSubmitFailureCode(undefined);
      setSubmitFailureMessage(undefined);
    },
    [
      t,
      submitSettings,
      handleStepChange,
      focusOnSubmitSuccess,
      navigateToCheckout,
      openPopup,
      setSubmitSuccessMessage,
      setSubmitFailureCode,
      setSubmitFailureMessage,
    ],
  );

  const handleSubmitFailure = useCallback(
    (error: unknown) => {
      const err = getErrorCode(error);
      setSubmitFailureCode(err);
      setSubmitSuccessMessage(undefined);
    },
    [setSubmitFailureCode, setSubmitSuccessMessage],
  );

  const handleCustomErrorMessages = useCallback(
    (error: unknown) => {
      const errors = getErrorList(error);

      const formCustomErrorMessage = getFormCustomErrorMessage(errors);
      if (formCustomErrorMessage) {
        setSubmitFailureMessage(formCustomErrorMessage);
      }

      const fieldCustomErrorMessages = getFieldCustomErrorMessages(errors);
      if (fieldCustomErrorMessages?.length) {
        onValidate(fieldCustomErrorMessages);
        handleScrollToFirstError?.(fieldCustomErrorMessages);
      }
    },
    [setSubmitFailureMessage, onValidate, handleScrollToFirstError],
  );

  const submitForm = useCallback(async () => {
    const errors = await validateAndScrollToError(values);

    onSubmit?.();
    reportSubmitFormBi({ status: BiFormSubmissionStatus.INIT });

    if (errors?.length) {
      reportSubmitFormBi({ status: BiFormSubmissionStatus.VALIDATION_FAILURE });
      return;
    }

    let response;

    try {
      panorama?.transaction(INTERACTIONS.SUBMIT_FORM).start();
      response = await submitAction(values);
    } catch (error) {
      onSubmitFailure?.(error);
      reportSubmitFormBi({ status: BiFormSubmissionStatus.FAILURE });
      handleCustomErrorMessages(error);
      handleSubmitFailure(error);
      throw error;
    }

    if (response) {
      onSubmitSuccess?.(response);
      reportSubmitFormBi({
        status: BiFormSubmissionStatus.SUCCESS,
        id: response.id,
      });
      panorama?.transaction(INTERACTIONS.SUBMIT_FORM).finish();

      fireSubmitSuccess({ ...values });
      handleSubmitSuccess(response);
      resetFormValues?.();
    }

    return response;
  }, [
    validateAndScrollToError,
    values,
    onSubmit,
    reportSubmitFormBi,
    panorama,
    submitAction,
    onSubmitFailure,
    handleCustomErrorMessages,
    handleSubmitFailure,
    onSubmitSuccess,
    fireSubmitSuccess,
    handleSubmitSuccess,
    resetFormValues,
  ]);

  return { submitForm };
};
